/**
 * ============================================================
 * CONTACT SLICE - Redux State Management for Contacts
 * ============================================================
 * 
 * This slice handles all contact-related state and API operations.
 * Uses middleware pattern for centralized API calls.
 * 
 * STATE STRUCTURE:
 * - contacts: Array of contact objects
 * - createContactLoading/Error/Success: States for POST /contact
 * - fetchContactsLoading/Error: States for GET /contact-list/{userId}
 * - deleteContactLoading/Error/Success: States for DELETE /delete-contact
 * 
 * API ENDPOINTS:
 * - POST /contact - Create new contact (requires auth)
 * - GET /contact-list/{userId} - Fetch user's contacts (requires auth)
 * - DELETE /delete-contact/{is_soft_delete}/{contact_id} - Delete a contact (requires auth)
 * 
 * HOW TO USE IN COMPONENTS:
 * 
 * 1. Import what you need:
 *    import { createContact, fetchContacts, selectContacts, ... } from '../../redux/contactSlice';
 * 
 * 2. Dispatch actions:
 *    dispatch(createContact({ contact_name: "John", ... }));
 *    dispatch(fetchContacts(userId));
 * 
 * 3. Read state with selectors:
 *    const contacts = useSelector(selectContacts);
 *    const loading = useSelector(selectCreateContactLoading);
 * ============================================================
 */

import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

// ============ INITIAL STATE ============
const initialState = {
    contacts: [],  // Array of contact objects

    // Create Contact states
    createContactLoading: false,   // true when POST /contact is in progress
    createContactError: null,      // Error message if create fails
    createContactSuccess: false,   // true after successful create

    // Fetch Contacts states
    fetchContactsLoading: false,   // true when GET /contact-list is in progress
    fetchContactsError: null,      // Error message if fetch fails

    // Delete Contact states
    deleteContactLoading: false,   // true when DELETE /delete-contact is in progress
    deleteContactError: null,      // Error message if delete fails
    deleteContactSuccess: false,   // true after successful delete
};

// ============ SLICE DEFINITION ============
const contactSlice = createSlice({
    name: 'contact',
    initialState,
    reducers: {
        // ---------- CREATE CONTACT REDUCERS ----------

        // Called when API request starts
        beginCreateContactLoader: (state) => {
            state.createContactLoading = true;
            state.createContactError = null;
            state.createContactSuccess = false;
        },

        // Called on successful API response
        setCreateContactData: (state, action) => {
            state.createContactLoading = false;
            state.createContactError = null;
            state.createContactSuccess = true;
            // Add new contact to local array
            if (action.payload) {
                state.contacts = [...state.contacts, action.payload];
            }
        },

        // Called on API error
        createContactError: (state, action) => {
            state.createContactLoading = false;
            state.createContactError = action.payload?.errorMessage || 'Failed to create contact';
            state.createContactSuccess = false;
        },

        // ---------- FETCH CONTACTS REDUCERS ----------

        // Called when fetch starts
        beginFetchContactsLoader: (state) => {
            state.fetchContactsLoading = true;
            state.fetchContactsError = null;
        },

        // Called on successful fetch
        setFetchContactsData: (state, action) => {
            state.fetchContactsLoading = false;
            state.fetchContactsError = null;
            state.contacts = action.payload || [];  // Replace contacts array
        },

        // Called on fetch error
        fetchContactsError: (state, action) => {
            state.fetchContactsLoading = false;
            state.fetchContactsError = action.payload?.errorMessage || 'Failed to fetch contacts';
        },

        // ---------- DELETE CONTACT REDUCERS ----------

        // Called when delete request starts
        beginDeleteContactLoader: (state) => {
            state.deleteContactLoading = true;
            state.deleteContactError = null;
            state.deleteContactSuccess = false;
        },

        // Called on successful delete
        setDeleteContactData: (state, action) => {
            state.deleteContactLoading = false;
            state.deleteContactError = null;
            state.deleteContactSuccess = true;
            // Remove deleted contact from local array using meta contactId
            const payload = action.payload;
            const deletedContactId = payload?.contactId || payload?.id || payload;
            if (deletedContactId) {
                state.contacts = state.contacts.filter(c => c.id !== deletedContactId);
            }
        },

        // Called on delete error
        deleteContactError: (state, action) => {
            state.deleteContactLoading = false;
            state.deleteContactError = action.payload?.errorMessage || 'Failed to delete contact';
            state.deleteContactSuccess = false;
        },

        // ---------- UTILITY REDUCERS ----------

        // Reset create status (call after showing success/error message)
        resetCreateContactStatus: (state) => {
            state.createContactLoading = false;
            state.createContactError = null;
            state.createContactSuccess = false;
        },

        // Reset delete status (call after showing success/error message)
        resetDeleteContactStatus: (state) => {
            state.deleteContactLoading = false;
            state.deleteContactError = null;
            state.deleteContactSuccess = false;
        },

        // Clear all contacts from state
        clearContacts: (state) => {
            state.contacts = [];
        },
    },
});

// ============ EXPORT ACTIONS ============
// These are auto-generated from reducers above
export const {
    beginCreateContactLoader,
    setCreateContactData,
    createContactError,
    beginFetchContactsLoader,
    setFetchContactsData,
    fetchContactsError,
    beginDeleteContactLoader,
    setDeleteContactData,
    deleteContactError,
    resetCreateContactStatus,
    resetDeleteContactStatus,
    clearContacts,
} = contactSlice.actions;

// ============ THUNK ACTIONS ============
// These dispatch to the API middleware

/**
 * CREATE CONTACT
 * 
 * Endpoint: POST /contact
 * Auth: Required (token in header)
 * 
 * @param {Object} contactData - The contact to create
 * @param {number} contactData.contact_type - 1=Family, 2=Friend, 3=Work
 * @param {string} contactData.contact_name - Full name
 * @param {string} contactData.email - Email address
 * @param {string} contactData.mobile - 10-digit phone number
 * @param {Array} contactData.media - Array of image objects [{file, file_extension, file_location, file_type}]
 * 
 * Usage: dispatch(createContact({ contact_type: 1, contact_name: "John", ... }))
 */
export const createContact = (contactData) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/contact',
            method: 'POST',
            data: contactData,
            onStart: beginCreateContactLoader.type,
            onSuccess: setCreateContactData.type,
            onError: createContactError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FETCH CONTACTS
 * 
 * Endpoint: GET /contact-list/{userId}
 * Auth: Required (token in header)
 * 
 * @param {number} userId - The user ID to fetch contacts for
 * 
 * Usage: dispatch(fetchContacts(4))  // where 4 is user ID
 * 
 * To get user ID from token:
 * const token = localStorage.getItem('authToken');
 * const payload = JSON.parse(atob(token.split('.')[1]));
 * const userId = payload.id;
 */
export const fetchContacts = (userId) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/contact-list/${userId}`,
            method: 'GET',
            onStart: beginFetchContactsLoader.type,
            onSuccess: setFetchContactsData.type,
            onError: fetchContactsError.type,
            showGlobalError: true,
        })
    );
};

/**
 * DELETE CONTACT
 * 
 * Endpoint: DELETE /delete-contact/{is_soft_delete}/{contact_id}
 * Auth: Required (token in header)
 * 
 * @param {number} contactId - The ID of the contact to delete
 * @param {boolean} isSoftDelete - Whether to soft delete (default: true)
 * 
 * Usage: dispatch(deleteContact(5))        // soft delete contact with ID 5
 *        dispatch(deleteContact(5, false)) // hard delete contact with ID 5
 */
export const deleteContact = (contactId, isSoftDelete = true) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/delete-contact/${isSoftDelete}/${contactId}`,
            method: 'DELETE',
            onStart: beginDeleteContactLoader.type,
            onSuccess: setDeleteContactData.type,
            onError: deleteContactError.type,
            showGlobalError: true,
            meta: { contactId },
        })
    );
};

// ============ SELECTORS ============
// Use these to read state in components: useSelector(selectContacts)
export const selectContacts = (state) => state.contact.contacts;
export const selectCreateContactLoading = (state) => state.contact.createContactLoading;
export const selectCreateContactError = (state) => state.contact.createContactError;
export const selectCreateContactSuccess = (state) => state.contact.createContactSuccess;
export const selectFetchContactsLoading = (state) => state.contact.fetchContactsLoading;
export const selectFetchContactsError = (state) => state.contact.fetchContactsError;
export const selectDeleteContactLoading = (state) => state.contact.deleteContactLoading;
export const selectDeleteContactError = (state) => state.contact.deleteContactError;
export const selectDeleteContactSuccess = (state) => state.contact.deleteContactSuccess;

// Export reducer for store configuration
export default contactSlice.reducer;
